import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiEndpoint } from '../../../config/api';
import { FaPlus, FaSearch, FaFilter, FaTicketAlt, FaChevronRight, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import MainHeader from '../../shared/MainHeader/MainHeader';
import './Support.css';

const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', waiting_on_user: 'Waiting on You',
  resolved: 'Resolved', closed: 'Closed'
};
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const CATEGORY_LABELS = {
  general: 'General', bug_report: 'Bug Report', feature_request: 'Feature Request',
  access_issue: 'Access Issue', billing: 'Billing', compliance: 'Compliance',
  data_request: 'Data Request', onboarding: 'Onboarding', other: 'Other'
};

const SupportTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ byStatus: {}, byPriority: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [showFilters, setShowFilters] = useState(false);

  const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.search) params.set('search', filters.search);

      const [ticketRes, statsRes] = await Promise.all([
        fetch(getApiEndpoint(`/api/support/tickets?${params}`), { headers }),
        fetch(getApiEndpoint('/api/support/tickets/stats'), { headers })
      ]);

      if (ticketRes.ok) {
        const data = await ticketRes.json();
        setTickets(data.tickets || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (e) {
      console.error('Failed to fetch tickets:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const activeCount = (stats.byStatus?.open || 0) + (stats.byStatus?.in_progress || 0) + (stats.byStatus?.waiting_on_user || 0);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="sup-page">
        <MainHeader />
        <div className="sup-container">
          <div className="sup-loading">
            <div className="sup-spinner" />
            <p>Loading tickets...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sup-page">
      <MainHeader />
      <div className="sup-container">
      {/* Header */}
      <div className="sup-header">
        <div className="sup-header-left">
          <FaTicketAlt className="sup-header-icon" />
          <div>
            <h1 className="sup-title">Support</h1>
            <p className="sup-subtitle">{activeCount} active ticket{activeCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button className="sup-btn sup-btn-primary" onClick={() => navigate('/support/new')}>
          <FaPlus /> New Ticket
        </button>
      </div>

      {/* Quick Stats */}
      <div className="sup-stats-row">
        <div className="sup-stat-card sup-stat-open">
          <span className="sup-stat-count">{stats.byStatus?.open || 0}</span>
          <span className="sup-stat-label">Open</span>
        </div>
        <div className="sup-stat-card sup-stat-progress">
          <span className="sup-stat-count">{stats.byStatus?.in_progress || 0}</span>
          <span className="sup-stat-label">In Progress</span>
        </div>
        <div className="sup-stat-card sup-stat-waiting">
          <span className="sup-stat-count">{stats.byStatus?.waiting_on_user || 0}</span>
          <span className="sup-stat-label">Waiting</span>
        </div>
        <div className="sup-stat-card sup-stat-resolved">
          <span className="sup-stat-count">{stats.byStatus?.resolved || 0}</span>
          <span className="sup-stat-label">Resolved</span>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="sup-toolbar">
        <div className="sup-search-wrap">
          <FaSearch className="sup-search-icon" />
          <input
            type="text"
            className="sup-search"
            placeholder="Search tickets..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>
        <button className={`sup-btn sup-btn-ghost ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
          <FaFilter /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="sup-filters">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button className="sup-btn sup-btn-ghost" onClick={() => setFilters({ status: '', priority: '', search: '' })}>
            Clear
          </button>
        </div>
      )}

      {/* Ticket List */}
      {tickets.length === 0 ? (
        <div className="sup-empty">
          <FaTicketAlt className="sup-empty-icon" />
          <h3>No tickets yet</h3>
          <p>Need help? Create a support ticket and our team will respond promptly.</p>
          <button className="sup-btn sup-btn-primary" onClick={() => navigate('/support/new')}>
            <FaPlus /> Create Your First Ticket
          </button>
        </div>
      ) : (
        <div className="sup-ticket-list">
          {tickets.map(t => (
            <Link to={`/support/tickets/${t.id}`} key={t.id} className="sup-ticket-row">
              <div className="sup-ticket-left">
                <div className="sup-ticket-ref-line">
                  <span className="sup-ticket-ref">{t.ticket_ref}</span>
                  <span className={`sup-badge sup-badge-status sup-status-${t.status}`}>{STATUS_LABELS[t.status]}</span>
                  <span className={`sup-badge sup-badge-priority sup-priority-${t.priority}`}>
                    {t.priority === 'urgent' && <FaExclamationTriangle />} {PRIORITY_LABELS[t.priority]}
                  </span>
                </div>
                <h3 className="sup-ticket-subject">{t.subject}</h3>
                <div className="sup-ticket-meta">
                  <span className="sup-ticket-category">{CATEGORY_LABELS[t.category] || t.category}</span>
                  {t.property_name && <span className="sup-ticket-property">{t.property_name}</span>}
                  <span className="sup-ticket-time"><FaClock /> {timeAgo(t.updated_at || t.created_at)}</span>
                </div>
              </div>
              <div className="sup-ticket-right">
                {t.unread_count > 0 && <span className="sup-unread-badge">{t.unread_count}</span>}
                {t.message_count > 0 && <span className="sup-msg-count">{t.message_count} msg{t.message_count !== 1 ? 's' : ''}</span>}
                <FaChevronRight className="sup-chevron" />
              </div>
            </Link>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default SupportTickets;
